export interface BlogPost {
  slug: string;
  title: string;
  seoTitle: string;
  description: string;
  excerpt: string;
  category: string;
  readTime: string;
  publishedDate: string;
  author: {
    name: string;
    role: string;
  };
  featuredImage?: string;
  featuredImageAlt?: string;
  // Optional override for the /blog index card thumbnail. Use this when the
  // full-article featured image carries a title overlay baked into the file
  // but the blog grid should show a clean, overlay-free photograph. The
  // article-page featured image, metadata, and schema keep `featuredImage`.
  blogCardImage?: string;
  blogCardImageAlt?: string;
}

export const FEATURED_IMAGE_BEFORE_AFTER =
  "https://vibe.filesafe.space/1779750798598342338/attachments/c2b28ebe-1857-4fd5-b0fb-b58f4fa569cc.webp";
export const SUPPORTING_IMAGE_BEFORE_AFTER_DECK =
  "https://vibe.filesafe.space/1779750798598342338/attachments/f440e043-363f-4fc5-8c5d-5398443ade29.webp";
export const SUPPORTING_IMAGE_COORDINATED_POOL_SPA =
  "https://vibe.filesafe.space/1779750798598342338/attachments/f9c13edb-b199-41bf-990e-3521b5f328cd.jpg";

// Article #2 — South Florida Pool Remodeling Guide.
export const FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE =
  "https://vibe.filesafe.space/1790702435770123164/attachments/4ec5a3e5-a03b-420e-b114-354ccc0cc51d.webp";
export const FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE_ALT =
  "Before-and-after Precision pool remodeling project showing a redesigned pool, water feature, coping, and surrounding deck";
export const FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE_CAPTION =
  "A Precision Pavers & Pool Design project showing how a broader remodel can coordinate the pool, coping, features, and surrounding deck.";
export const SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK =
  "https://vibe.filesafe.space/1790702435770123164/attachments/0f5b82ad-0979-4276-84ab-f6fff965d227.jpg";
export const SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK_ALT =
  "Overhead view of a completed South Florida pool remodel showing the pool perimeter, coping, and surrounding hardscape deck area";
export const SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_DECK_CAPTION =
  "Pool deck and paver work is often one of the largest variables in remodeling scope and cost because square footage and site conditions vary from property to property.";
export const SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED =
  "https://vibe.filesafe.space/1790702435770123164/attachments/2fbca49f-f69a-4c14-b4ef-edda57639442.jpg";
export const SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED_ALT =
  "Completed Precision pool remodeling project with coordinated pool finish, coping, shallow area, and surrounding deck";
export const SUPPORTING_IMAGE_SOUTH_FLORIDA_GUIDE_COORDINATED_CAPTION =
  "Coordinating the pool finish, coping, deck, and surrounding space early helps the completed project feel intentional rather than pieced together.";

// Article #3 — Signs Your Pool Needs Resurfacing.
export const FEATURED_IMAGE_SIGNS_RESURFACING =
  "https://vibe.filesafe.space/1790702435770123164/attachments/b598476c-8703-46ad-9b79-f10769945495.webp";
export const FEATURED_IMAGE_SIGNS_RESURFACING_ALT =
  "Before-and-after Precision pool project illustrating an updated pool interior, coping, and surrounding deck";
export const FEATURED_IMAGE_SIGNS_RESURFACING_CAPTION =
  "An authentic Precision Pavers & Pool Design project. Visible symptoms can help guide the next conversation, but photographs alone cannot determine whether a pool needs resurfacing or further evaluation.";
export const SUPPORTING_IMAGE_SIGNS_RESURFACING =
  "https://vibe.filesafe.space/1790702435770123164/assets/9445c55f-a912-41d9-8a4b-715bd0798638.webp";
export const SUPPORTING_IMAGE_SIGNS_RESURFACING_ALT =
  "Completed Precision pool project showing the interior finish, waterline tile, coping, and surrounding deck";
export const SUPPORTING_IMAGE_SIGNS_RESURFACING_CAPTION =
  "The finish, tile, coping, and deck should be considered together, but a completed appearance does not reveal what caused the original surface concerns.";
export const SUPPORTING_IMAGE_SIGNS_EVALUATE =
  "https://vibe.filesafe.space/1790702435770123164/attachments/933246e8-81e4-45b6-91fb-edab52ecc2ac.webp";
export const SUPPORTING_IMAGE_SIGNS_EVALUATE_ALT =
  "Before-and-after Precision pool remodeling project involving the pool edge and surrounding deck";
export const SUPPORTING_IMAGE_SIGNS_EVALUATE_CAPTION =
  "When concerns extend beyond the interior finish, the connected pool edge and deck should be evaluated before resurfacing is treated as the complete solution.";

// Article #4 — Best Pool Deck Materials for South Florida.
import { FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS_DATA_URI } from "./featuredImageArt4";
import {
  SUPPORTING_IMAGE_COMPARISON_DATA_URI,
  SUPPORTING_IMAGE_SELECTION_DATA_URI,
} from "./supportingImagesArt4";

export const FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS =
  FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS_DATA_URI;
export const FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS_ALT =
  "Completed Precision pool project showing a light-colored pool deck surrounding a residential swimming pool";
export const FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS_CAPTION =
  "An authentic Precision Pavers & Pool Design project showing how the pool deck frames the entire outdoor area. The right material depends on the specific product, sun exposure, design goals, maintenance expectations, and project conditions.";
export const SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_COMPARISON =
  SUPPORTING_IMAGE_COMPARISON_DATA_URI;
export const SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_COMPARISON_ALT =
  "Completed Precision pool project showing a warm-toned deck surrounding a swimming pool and raised spa";
export const SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_COMPARISON_CAPTION =
  "Material color, pattern, surface finish, coping, and the surrounding architecture all influence how a pool deck looks and functions as a complete space.";
export const SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_SELECTION =
  SUPPORTING_IMAGE_SELECTION_DATA_URI;
export const SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_SELECTION_ALT =
  "Completed Precision pool project showing a light-colored pool deck with lounge seating beside the water";
export const SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_SELECTION_CAPTION =
  "Reviewing a material across a finished outdoor area provides more context than choosing from a small sample or screen image alone.";

// Article #5 — South Florida Pool Finish Comparison.
export const FEATURED_IMAGE_POOL_FINISH_COMPARISON =
  "https://vibe.filesafe.space/1790702435770123164/attachments/d0854382-87fb-4b0c-a71b-4bc52076ee96.jpg";
export const FEATURED_IMAGE_POOL_FINISH_COMPARISON_ALT =
  "Authentic Precision pool project showing how water depth and lighting affect the appearance of a pool finish";
export const FEATURED_IMAGE_POOL_FINISH_COMPARISON_CAPTION =
  "An authentic Precision Pavers & Pool Design project. Finish color helps shape the appearance of the water, but depth, sunlight, surroundings, and viewing conditions also influence the final result.";
export const SUPPORTING_IMAGE_POOL_FINISH_COMPARISON =
  "https://vibe.filesafe.space/1790702435770123164/attachments/674d2b95-d167-4551-9059-095887285644.jpg";
export const SUPPORTING_IMAGE_POOL_FINISH_COMPARISON_ALT =
  "Precision pool-resurfacing project showing the interior-finish application process in a drained swimming pool";
export const SUPPORTING_IMAGE_POOL_FINISH_COMPARISON_CAPTION =
  "An authentic Precision project during pool resurfacing. The finish category is only part of the decision; preparation, application, exposure, and startup also influence the completed surface.";
export const SUPPORTING_IMAGE_POOL_FINISH_SELECTION =
  "https://vibe.filesafe.space/1790702435770123164/attachments/f272e3ca-ca08-49fb-a577-0a22812e8447.webp";
export const SUPPORTING_IMAGE_POOL_FINISH_SELECTION_ALT =
  "Completed Precision pool interior showing natural color and tonal variation across the finished surface";
export const SUPPORTING_IMAGE_POOL_FINISH_SELECTION_CAPTION =
  "An authentic Precision project showing a completed pool interior before filling. Lighting, surface texture, application, and viewing conditions can make color and tonal variation more noticeable.";

// Article #6 — Should You Replace Pool Tile, Coping, and Decking During Resurfacing?
export const FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING =
  "https://vibe.filesafe.space/1790702435770123164/attachments/ff77bec3-a31e-497a-ba26-9ecd4e0e2614.webp";
export const FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING_ALT =
  "Before-and-after Precision pool project showing coordinated updates to the pool interior, coping, and surrounding deck";
export const FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING_CAPTION =
  "An authentic Precision Pavers & Pool Design project showing how pool resurfacing, coping, and deck improvements can be coordinated when the condition and project scope support broader replacement.";
export const SUPPORTING_IMAGE_REPLACE_TILE_COPING =
  "https://vibe.filesafe.space/1790702435770123164/attachments/70816a63-6d6f-4042-ba44-ee76302461a5.webp";
export const SUPPORTING_IMAGE_REPLACE_TILE_COPING_ALT =
  "Authentic Precision pool project showing the relationship between the interior finish, decorative tile, coping, and surrounding deck";
export const SUPPORTING_IMAGE_REPLACE_TILE_COPING_CAPTION =
  "An authentic Precision Pavers & Pool Design project. The pool interior, tile details, coping, and deck are separate components, but their colors, elevations, and transitions should be planned together.";
export const SUPPORTING_IMAGE_REPLACE_DECK_COORDINATION =
  "https://vibe.filesafe.space/1790702435770123164/attachments/90e59ee1-3ebc-4f0b-910f-699def8d0d0f.jpg";
export const SUPPORTING_IMAGE_REPLACE_DECK_COORDINATION_ALT =
  "Completed Precision pool project with coordinated waterline tile, coping, and light-colored surrounding deck";
export const SUPPORTING_IMAGE_REPLACE_DECK_COORDINATION_CAPTION =
  "An authentic Precision project showing the pool edge and surrounding deck as one coordinated outdoor space. Material color, scale, transitions, and the components that remain all influence the completed design.";

// Article #7 — How Much Does Pool Resurfacing Cost in South Florida?
export const FEATURED_IMAGE_POOL_RESURFACING_COST =
  "https://vibe.filesafe.space/1790702435770123164/attachments/86a28385-a434-4c26-a3dd-365df2516877.webp";
export const FEATURED_IMAGE_POOL_RESURFACING_COST_ALT =
  "Before-and-after Precision pool project showing surface preparation and the completed resurfaced pool with updated surrounding pavers";
export const FEATURED_IMAGE_POOL_RESURFACING_COST_CAPTION =
  "An authentic Precision Pavers & Pool Design project. Pool-resurfacing prices depend on the selected finish, preparation requirements, pool condition, and any additional work included in the scope.";
export const SUPPORTING_IMAGE_POOL_RESURFACING_COST_PREPARATION =
  "https://vibe.filesafe.space/1790702435770123164/attachments/2f61041c-acda-4e95-b7ed-f12c885f0811.jpg";
export const SUPPORTING_IMAGE_POOL_RESURFACING_COST_PREPARATION_ALT =
  "Pool resurfacing crew working inside a drained pool during surface preparation and finish application";
export const SUPPORTING_IMAGE_POOL_RESURFACING_COST_PREPARATION_CAPTION =
  "Surface preparation and the condition beneath the existing finish can materially affect the scope and cost of a pool-resurfacing project.";
export const SUPPORTING_IMAGE_POOL_RESURFACING_COST_STARTUP =
  "https://vibe.filesafe.space/1790702435770123164/attachments/170df927-c291-4299-9263-c9bb0c0a8d97.webp";
export const SUPPORTING_IMAGE_POOL_RESURFACING_COST_STARTUP_ALT =
  "Recently resurfaced swimming pool during the refill and initial startup stage";
export const SUPPORTING_IMAGE_POOL_RESURFACING_COST_STARTUP_CAPTION =
  "Refilling, initial water balancing, brushing, and follow-up startup service may be included or excluded depending on the written proposal.";

// Article #8 — Pool Resurfacing Process: What to Expect Before, During, and
// After. Image constants remain empty until approved authentic Precision
// images are supplied in Prompt #2.
export const FEATURED_IMAGE_POOL_RESURFACING_PROCESS =
  "https://vibe.filesafe.space/1790702435770123164/attachments/50eec7e3-372c-4667-ad4d-1524749b0799.webp";
export const FEATURED_IMAGE_POOL_RESURFACING_PROCESS_ALT =
  "Precision pool project during interior surface preparation as part of the pool resurfacing process";
export const FEATURED_IMAGE_POOL_RESURFACING_PROCESS_CAPTION =
  "An authentic Precision Pavers & Pool Design project during pool-interior preparation. The required steps can vary depending on the existing surface and conditions discovered after draining.";
export const SUPPORTING_IMAGE_POOL_RESURFACING_PROCESS_PREPARATION =
  "https://vibe.filesafe.space/1790702435770123164/attachments/0af0d098-ca08-4203-8504-7f621074d6cc.jpg";
export const SUPPORTING_IMAGE_POOL_RESURFACING_PROCESS_PREPARATION_ALT =
  "Drained swimming pool during the surface-preparation stage of a resurfacing project";
export const SUPPORTING_IMAGE_POOL_RESURFACING_PROCESS_PREPARATION_CAPTION =
  "Surface preparation occurs after the pool is drained. The exact preparation method depends on the existing finish and the conditions found during evaluation.";
export const SUPPORTING_IMAGE_POOL_RESURFACING_PROCESS_REFILL =
  "https://vibe.filesafe.space/1790702435770123164/attachments/ea65b5a1-25a2-46c2-b8cb-37fc8718ef80.jpg";
export const SUPPORTING_IMAGE_POOL_RESURFACING_PROCESS_REFILL_ALT =
  "Pool-finishing crew applying a new interior surface inside a drained swimming pool";
export const SUPPORTING_IMAGE_POOL_RESURFACING_PROCESS_REFILL_CAPTION =
  "A pool-finishing crew works across the drained shell during the finish-application stage. Application procedures and crew requirements vary by the selected finish system.";

// Article #9 — Best Pool Coping Materials for South Florida. Image constants
// live in src/data/poolCopingMaterialsData.ts and remain empty until approved
// authentic Precision images are supplied in Prompt #2.
import {
  FEATURED_IMAGE_POOL_COPING_MATERIALS,
  FEATURED_IMAGE_POOL_COPING_MATERIALS_ALT,
} from "./poolCopingMaterialsData";

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "pool-resurfacing-vs-pool-remodeling",
    title: "Pool Resurfacing vs. Pool Remodeling: Which Does Your Pool Need?",
    seoTitle: "Pool Resurfacing vs. Remodeling: Which Do You Need?",
    description:
      "Learn the difference between pool resurfacing and remodeling, what each project can include, and how to choose the right scope for your South Florida pool.",
    excerpt:
      "Resurfacing renews the interior finish. Remodeling can address the finish plus tile, coping, decking, features, and broader design or functional goals. Here’s how to evaluate the right scope.",
    category: "Pool Remodeling & Care",
    readTime: "8 min read",
    publishedDate: "2026-09-28",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_BEFORE_AFTER,
    featuredImageAlt:
      "Before-and-after Precision pool remodeling project showing an updated pool interior, coping, and surrounding pool deck",
  },
  {
    slug: "south-florida-pool-remodeling-guide",
    title:
      "South Florida Pool Remodeling Guide: Resurfacing, Tile, Coping, Decks, Costs, and Planning",
    seoTitle: "South Florida Pool Remodeling Guide | Precision",
    description:
      "Plan a South Florida pool remodel by understanding resurfacing, tile, coping, pool decks, costs, sequencing, and how to define the right project scope.",
    excerpt:
      "A practical guide to planning a South Florida pool remodel, including resurfacing, waterline tile, coping, pool decks, project scope, costs, sequencing, and material coordination.",
    category: "Pool Remodeling Guide",
    readTime: "Est. 10 min read",
    publishedDate: "2026-10-05",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE || undefined,
    featuredImageAlt: FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE_ALT,
  },
  {
    slug: "signs-pool-needs-resurfacing",
    title:
      "Signs Your Pool Needs Resurfacing—and Signs the Problem Goes Deeper",
    seoTitle: "7 Signs Your Pool Needs Resurfacing | South Florida",
    description:
      "Learn which pool symptoms may point to a worn interior finish—and which cracks, water loss, movement, or recurring problems need evaluation first.",
    excerpt:
      "Roughness, stains, discoloration, cracking, and water loss do not always point to the same solution. Learn which signs may support resurfacing and which need further evaluation.",
    category: "Pool Care & Diagnosis",
    readTime: "Est. 8 min read",
    publishedDate: "2026-10-08",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_SIGNS_RESURFACING || undefined,
    featuredImageAlt: FEATURED_IMAGE_SIGNS_RESURFACING_ALT,
  },
  {
    slug: "best-pool-deck-materials-south-florida",
    title: "Best Pool Deck Materials for South Florida",
    seoTitle: "Best Pool Deck Materials for South Florida",
    description:
      "Compare pool deck materials for South Florida, including concrete pavers, travertine, marble, porcelain, and other options based on heat, traction, maintenance, drainage, design, and budget.",
    excerpt:
      "Compare pool deck materials for South Florida, including concrete pavers, travertine, marble, porcelain, and other options based on heat, traction, maintenance, drainage, design, and budget.",
    category: "Pool Decks & Materials",
    readTime: "Est. 9 min read",
    publishedDate: "2026-10-13",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS || undefined,
    featuredImageAlt: FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS_ALT,
    // The Article #4 full-article featured image has a title overlay baked
    // into the file ("BEST POOL DECK MATERIALS FOR SOUTH FLORIDA"), which is
    // appropriate on the article page but inconsistent with the clean
    // category-only thumbnails used by every other blog card. Use the clean,
    // authentic comparison photograph for the /blog index card so the grid
    // is uniform, while the article page, metadata, and schema keep the
    // original featured image.
    blogCardImage:
      SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_COMPARISON || undefined,
    blogCardImageAlt: SUPPORTING_IMAGE_BEST_POOL_DECK_MATERIALS_COMPARISON_ALT,
  },
  {
    slug: "pool-finish-comparison-south-florida",
    title:
      "South Florida Pool Finish Comparison: Plaster vs. Quartz vs. Pebble",
    seoTitle: "Pool Finish Comparison: Plaster vs. Quartz vs. Pebble",
    description:
      "Compare plaster, quartz, Diamond Brite, pebble, and other pool finishes by texture, appearance, water color, maintenance, variation, and project fit.",
    excerpt:
      "Compare plaster, quartz, Diamond Brite, pebble, and other pool finishes by texture, appearance, water color, maintenance, variation, and project fit.",
    category: "Pool Remodeling & Resurfacing",
    readTime: "Est. 10 min read",
    publishedDate: "2026-10-16",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_POOL_FINISH_COMPARISON || undefined,
    featuredImageAlt: FEATURED_IMAGE_POOL_FINISH_COMPARISON_ALT,
  },
  {
    slug: "replace-pool-tile-coping-decking-during-resurfacing",
    title:
      "Should You Replace Pool Tile, Coping, and Decking During Resurfacing?",
    seoTitle: "Replace Tile, Coping & Decking During Resurfacing?",
    description:
      "Learn when pool tile, coping, and decking should be replaced during resurfacing—and when sound components may remain as part of a coordinated remodel.",
    excerpt:
      "Learn when pool tile, coping, and decking should be replaced during resurfacing—and when sound components may remain as part of a coordinated remodel.",
    category: "Pool Remodeling & Resurfacing",
    readTime: "Est. 10 min read",
    publishedDate: "2026-10-17",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING || undefined,
    featuredImageAlt: FEATURED_IMAGE_REPLACE_TILE_COPING_DECKING_ALT,
  },
  {
    slug: "pool-resurfacing-cost-south-florida",
    title: "How Much Does Pool Resurfacing Cost in South Florida?",
    seoTitle: "Pool Resurfacing Cost in South Florida: 2026 Guide",
    description:
      "Learn what pool resurfacing may cost in South Florida, which factors change the price, what a quote should include, and how to compare estimates accurately.",
    excerpt:
      "Learn what pool resurfacing may cost in South Florida, which factors change the price, what a quote should include, and how to compare estimates accurately.",
    category: "Pool Remodeling & Resurfacing",
    readTime: "Est. 10 min read",
    publishedDate: "2026-10-19",
    author: {
      name: "Precision Pavers & Pool Design",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_POOL_RESURFACING_COST || undefined,
    featuredImageAlt: FEATURED_IMAGE_POOL_RESURFACING_COST_ALT,
  },
  {
    slug: "pool-resurfacing-process-south-florida",
    title: "Pool Resurfacing Process: What to Expect Before, During, and After",
    seoTitle: "Pool Resurfacing Process in South Florida: What to Expect",
    description:
      "Understand the South Florida pool resurfacing process, from planning and draining through surface preparation, finish application, refill, startup, and early care.",
    excerpt:
      "Understand the South Florida pool resurfacing process, from planning and draining through surface preparation, finish application, refill, startup, and early care.",
    category: "Pool Remodeling & Resurfacing",
    readTime: "Est. 10 min read",
    publishedDate: "2026-10-20",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_POOL_RESURFACING_PROCESS || undefined,
    featuredImageAlt: FEATURED_IMAGE_POOL_RESURFACING_PROCESS_ALT,
  },
  {
    slug: "best-pool-coping-materials-south-florida",
    title:
      "Best Pool Coping Materials for South Florida: How to Choose the Right Pool Edge",
    seoTitle: "Best Pool Coping Materials for South Florida",
    description:
      "Compare pool coping materials for South Florida, including travertine, marble, concrete, paver, porcelain, and other options by heat, traction, maintenance, appearance, and project fit.",
    excerpt:
      "Compare pool coping materials for South Florida, including travertine, marble, concrete, paver, porcelain, and other options by heat, traction, maintenance, appearance, and project fit.",
    category: "Pool Decks & Materials",
    readTime: "Est. 9 min read",
    publishedDate: "2026-10-20",
    author: {
      name: "Precision Pavers & Pool Design Team",
      role: "South Florida Outdoor Living Specialists",
    },
    featuredImage: FEATURED_IMAGE_POOL_COPING_MATERIALS || undefined,
    featuredImageAlt: FEATURED_IMAGE_POOL_COPING_MATERIALS_ALT,
  },
];
